import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { PlanarGraphPerimeterInfo } from "building-types/planarGraphs/planarGraphPerimeterInfo";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import type { SolidWall } from "building-types/walls/wallTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { RWTable } from "common/data/table";
import { solidWallWithOpenings } from "./solidWall";
import { getWallPath } from "./wall";
import { wallSideNew } from "./wallSideBasics";

export class SolidWallChangeSet {
  readonly doorsRemoved: string[] = [];
  readonly windowsRemoved: string[] = [];
  readonly doorsAdded: GeneratedDoorPlacement[] = [];
  readonly windowsAdded: GeneratedWindowPlacement[] = [];

  constructor(
    readonly wall: SolidWall,
    readonly levelId: string,
    readonly wallSystem: WallSystem,
    readonly planarGraphPerimeter: PlanarGraphPerimeterInfo,
    readonly mirror: boolean,
  ) {
    if (!(wall)) {
      console.assert(false, "wall in SolidWallChangeSet is empty");
    }
  }

  removeWindow(window: GeneratedWindowPlacement): SolidWallChangeSet {
    const addWindowIdx = this.windowsAdded.indexOf(window);
    if (addWindowIdx >= 0) {
      this.windowsAdded.splice(addWindowIdx, 1);
      return this;
    }
    this.windowsRemoved.push(window.id);
    return this;
  }

  apply(doors: RWTable<GeneratedDoorPlacement>, windows: RWTable<GeneratedWindowPlacement>): SolidWall {
    const { doorsRemoved, windowsRemoved, doorsAdded, windowsAdded } = this;
    const { leftSide, rightSide } = this.wall;
    const { mass } = this.wall;
    const path = getWallPath(this.wall);

    const newLeftDoors = leftSide.doors.filter((x) => doorsRemoved.indexOf(x.id) === -1);
    const newRightDoors = rightSide.doors.filter((x) => doorsRemoved.indexOf(x.id) === -1);
    const newLeftWindows = leftSide.windows.filter((x) => windowsRemoved.indexOf(x.id) === -1);
    const newRightWindows = rightSide.windows.filter((x) => windowsRemoved.indexOf(x.id) === -1);

    for (const doorId of doorsRemoved) {
      delete doors[doorId];
    }

    for (const windowId of windowsRemoved) {
      delete windows[windowId];
    }

    for (const door of doorsAdded) {
      doors[door.id] = door;

      if (door.frontRoom === path.leftRoom) {
        newLeftDoors.push(door);
      } else {
        newRightDoors.push(door);
      }
    }

    for (const window of windowsAdded) {
      windows[window.id] = window;

      if (window.frontRoom === path.leftRoom) {
        newLeftWindows.push(window);
      } else {
        newRightWindows.push(window);
      }
    }

    const newLeftSide = wallSideNew({
      windows: newLeftWindows,
      doors: newLeftDoors,
    });

    const newRightSide = wallSideNew({
      windows: newRightWindows,
      doors: newRightDoors,
    });

    return solidWallWithOpenings({
      id: this.wall.id,
      levelId: this.levelId,
      path,
      mass,
      leftSide: newLeftSide,
      rightSide: newRightSide,
      wallMassSystem: this.wallSystem as WallMassSystem,
      mirror: this.mirror,
      issues: [], // issues are omitted from this API until it becomes public
    });
  }
}
